// @flow
import type {Saga, SagaIterator} from 'redux-saga';

import {CATEGORY, APP, PRODUCT} from 'constants/actions';
import {PRODUCT_GENDER, GENDER_UNISEX_CODE, GENDER_ID_UNISEX} from 'constants/gender';
import {CATEGORY_STORE} from 'constants/category';
import {ActiveFiltersType} from 'reducers/category/activeFilters/activeFiltersType';

import {sessionStore} from 'libs/storage/storage';
import {setGender} from 'actions/gender/gender';
import {put, takeEvery, call, take, select, all} from 'redux-saga/effects';
import {getCurrentPage} from 'selectors/app/app';
import {getCategory} from 'selectors/category/category';
import {getActiveProduct} from 'selectors/product/product';
import {getActiveFilters} from 'selectors/filters/filters';
import type {ActionType} from 'constants/flow/flowTypes';

import {getGenderFromFilters} from './helpers/helpers';

/**
 * Get gender code from category
 *
 * @returns {SagaIterator} - gender code
 */
export function* getCategoryGender(): SagaIterator {
    yield take(`${CATEGORY.GET_CATEGORY}_FULFILLED`);
    const category = yield select(getCategory);

    return category.gender;
}

/**
 * Get gender code from filters
 *
 * @returns {SagaIterator} - gender code
 */
export function* getCategoryGenderByFilters(): SagaIterator {
    yield take(CATEGORY.SET_ACTIVE_FILTERS);
    const filters = yield select(getActiveFilters);

    return filters.gender ? yield call(getGenderFromFilters, filters.gender) : null;
}

/**
 * Get gender code from category information or filters on page view
 *
 * @returns {SagaIterator} - gender code
 */
export function* getCategoryGenderOnPageView(): SagaIterator {
    const [categoryGender, filterGender] = yield all([
        call(getCategoryGender),
        call(getCategoryGenderByFilters),
    ]);

    return filterGender || categoryGender;
}

/**
 * Set gender code or set default unisex gender
 * @param {number} code - gender code
 */
export function* setGenderCode(code: number | null): SagaIterator {
    yield put(setGender(code || GENDER_UNISEX_CODE));
}

/**
 * Set actual gender by filter category
 */
export function* setActualGenderByFilter({
    payload: filters,
}: ActionType<ActiveFiltersType>): SagaIterator {
    const genderCode = filters.gender ? yield call(getGenderFromFilters, filters.gender) : null;

    // This action should not calling when filters empty, the situation is such that it turns out gender is recorded twice.
    // @TODO Need to refactoring, this saga, because it looks like a crutch
    if (genderCode) {
        yield call(setGenderCode, genderCode);
    }
}

/**
 * Get product gender
 * @return {Saga<number>} Product gender identification
 */
export function* getProductGender(): Saga<number> {
    let product = yield select(getActiveProduct);

    if (!product) {
        yield take(PRODUCT.SET_ACTIVE_PRODUCT_ID);
        product = yield select(getActiveProduct);
    }

    const categoryFromStore = yield call([sessionStore, 'get'], CATEGORY_STORE);

    const gender = product.gender ? product.gender[0] : GENDER_ID_UNISEX;
    return categoryFromStore ? categoryFromStore.gender : PRODUCT_GENDER[gender];
}

/**
 * Set actual gender identification
 */
export function* setActualGenderOnPageView(): SagaIterator {
    const {page} = yield select(getCurrentPage);
    let genderCode;

    if (page === 'category') {
        genderCode = yield call(getCategoryGenderOnPageView);
    }

    if (page === 'product') {
        genderCode = yield call(getProductGender);
    }

    yield call(setGenderCode, genderCode);
}

/**
 * Root gender saga
 */
export default function* genderSaga(): SagaIterator {
    yield takeEvery(APP.SAVE_LOCATION, setActualGenderOnPageView);
    yield takeEvery(CATEGORY.SET_ACTIVE_FILTERS, setActualGenderByFilter);
}
